import { ApolloError } from '@apollo/client';
import React from 'react';
import { useFormContext, FormState, UseFormReturn, FieldValues } from 'react-hook-form';

import Form, { FormProps, SubmitError, FormOptionsContext } from '$/components/ui/Form';
import Input from './ui/Input/Basics/Input';

interface Errors {
  [name: string]: string;
}

interface ValidationError {
  property: string;
  constraints: {
    [name: string]: string;
  };
}

export function buildSubmitError(e: ApolloError): SubmitError {
  const error = e.graphQLErrors?.[0];

  if (error) {
    // @ts-ignore
    if (error.extensions?.exception?.validationErrors) {
      // @ts-ignore
      const errors = error.extensions.exception.validationErrors.reduce(
        (errors: Errors, validationError: ValidationError) => {
          const errorMessageKey = Object.keys(validationError.constraints)[0];
          const errorMessage = errorMessageKey && validationError.constraints[errorMessageKey];
          return {
            ...errors,
            [validationError.property]: errorMessage,
          };
        },
        {}
      );

      return new SubmitError(errors);
    }

    if (error.extensions?.code === 'MAX_VIDEO_SIZE') {
      return new SubmitError({
        _: 'MAX_VIDEO_SIZE',
      });
    }

    if (error.message) {
      return new SubmitError({
        _: error.message,
      });
    }
  }

  return new SubmitError({ _: e.message });
}

const UnsavedChangesDialog: React.FC = () => {
  const { formState } = useFormContext();

  // on page change using browser
  React.useLayoutEffect(() => {
    const handleBeforeUnload = (event: Event): void => {
      if (formState.isDirty) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return (): void => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [formState.isDirty]);

  return null;
};

const ApolloForm: React.FC<FormProps<FieldValues>> = React.memo(
  ({ showUnsavedChangesDialog, onSubmit, ...props }) => {
    const onFormSubmit = React.useCallback(
      async (
        values: FieldValues,
        defaultValues: FieldValues,
        formState?: FormState<FieldValues>,
        formHandlers?: UseFormReturn
      ): Promise<void> => {
        try {
          onSubmit && (await onSubmit(values, defaultValues, formState, formHandlers));
        } catch (error) {
          const e = error as Error | ApolloError;
          console.error('Apollo Form Error: ', e);

          if (e.name === 'SubmitError') {
            throw e;
          }

          if ((e as ApolloError).graphQLErrors) {
            throw buildSubmitError(e as ApolloError);
          }

          throw e;
        }
      },
      [onSubmit]
    );

    return (
      <Form
        {...props}
        showUnsavedChangesDialog={showUnsavedChangesDialog || false}
        onSubmit={onFormSubmit}
      >
        {showUnsavedChangesDialog && <UnsavedChangesDialog />}
        <Form.Field name='id' className='hidden' component={<Input type='hidden' />} />
        {props.children}
      </Form>
    );
  }
);
ApolloForm.displayName = 'ApolloForm';

export default ApolloForm;
